import makeApiCall from "./master/axiosIndex";

// Task Monitoring: which BOQ lines (tasks) the site is ASKED ABOUT each day,
// and between which dates.
//
// Monitoring is a FOCUS filter, never a derivation filter. The execution
// engine keeps exploding every normed line and the task_actuals projection
// stays complete; the capture screens read the tracked set for their WORKING
// date and hide the rest.
//
// All dates cross the wire as 'YYYY-MM-DD' strings; '' means "not set" (an
// open-ended activeTo). Every decimal is a STRING; '' means none.
//
// gRPC OMITS false / 0 / '' fields on the way back, so a task that is not
// tracked arrives with no `tracked` key at all. Normalise every optional field
// with a default before it reaches state — see
// execution/taskMonitoring.ts `normalizeTaskMonitoringTree`.

export interface TaskMonitoringTask {
  taskId: number;
  taskSequenceId: string; // 'AE-0101/B3.1.1(1)' — THE unique identity
  internalTaskNumber: string; // bare 'B3.1.1' — NOT unique, never label by it
  taskName: string;
  workQuantity: string; // '' when none
  workUomName: string;
  taskStatus: string;
  // Norm availability — what tracking this task would actually surface.
  hasExecutionNorm: boolean; // Civil: derives from the face's advance
  materialLineCount: number;
  manpowerLineCount: number;
  machineryLineCount: number;
  // The tracking row, when one exists.
  tracked: boolean;
  activeFrom: string; // 'YYYY-MM-DD' or ''
  activeTo: string; // '' = open-ended
  remarks: string;
  trackedOnDate: boolean; // tracked AND the window covers asOfDate
  monitoringId: number;
}

export interface TaskMonitoringActivity {
  activityId: number;
  activityName: string;
  activitySequenceId: string;
  taskCount: number;
  trackedCount: number;
  activeOnDateCount: number;
  normedTaskCount: number;
  tasks: TaskMonitoringTask[];
}

export interface TaskMonitoringPhase {
  phaseId: number; // 0 = tasks with no phase
  phaseName: string;
  phaseSequenceId: string;
  taskCount: number;
  trackedCount: number;
  activeOnDateCount: number;
  activities: TaskMonitoringActivity[];
}

export interface TaskMonitoringTree {
  projectId: number;
  asOfDate: string;
  taskCount: number;
  trackedCount: number;
  activeOnDateCount: number;
  // Tracked tasks that carry no execution norm — they will never show in
  // Civil, which is usually a configuration gap worth a warning tile.
  trackedWithoutNormCount: number;
  activitiesWithTrackedCount: number;
  phases: TaskMonitoringPhase[];
}

export interface SaveTaskMonitoringItem {
  taskId: number;
  tracked: boolean; // false = untrack (soft delete)
  activeFrom: string; // required when tracked
  activeTo: string; // '' = open-ended
  remarks: string;
}

export interface SaveTaskMonitoringResponse {
  id: string;
  message: string;
  // Non-blocking, e.g. "3 tracked tasks have no execution norm". Shown as a
  // warning toast; never stops the save.
  warning: string;
  trackedCount: number;
  untrackedCount: number;
  skippedCount: number; // untrack of a task that was not tracked
}

export const taskMonitoringApis = {
  // Phase -> activity -> task tree with each task's tracking window and norm
  // availability, evaluated as of ONE date. `asOfDate` only changes the
  // "active on date" evaluation; omitted = today on the server clock.
  getTree: (projectId: number | string, asOfDate?: string): Promise<any> => {
    const params: Record<string, any> = {};
    if (asOfDate !== undefined && asOfDate !== null && `${asOfDate}`.trim() !== "") {
      params.asOfDate = asOfDate;
    }
    return makeApiCall({
      url: `/task-monitoring/project/${projectId}/tree`,
      method: "get",
      params,
    });
  },

  // UPSERT per item — NOT a replace-set. Send only the rows the user changed;
  // rows not mentioned are left alone. A taskId may appear once per save.
  save: (payload: {
    projectId: number;
    items: SaveTaskMonitoringItem[];
  }): Promise<any> =>
    makeApiCall({
      url: `/task-monitoring/save`,
      method: "post",
      data: payload,
    }),

  // The cheap lookup the capture screens call: task ids tracked on the
  // WORKING date (never now()), optionally narrowed to one activity.
  listMonitored: (
    projectId: number | string,
    date: string,
    activityId?: number | string
  ): Promise<any> => {
    const params: Record<string, any> = { date };
    if (activityId !== undefined && activityId !== null && `${activityId}`.trim() !== "") {
      params.activityId = activityId;
    }
    return makeApiCall({
      url: `/task-monitoring/project/${projectId}/monitored`,
      method: "get",
      params,
    });
  },
};
